import { useCallback, useEffect, useState } from 'react';
import { shallowEqual } from 'react-redux';
import { useNavigate } from 'react-router';

import { useAppDispatch, useAppSelector } from '@/hooks/redux-helpers';
import { selectProfileWalletsByUserId } from '@/store/selectors';
import { IWallet } from '@/features/wallets/store/models/wallet.model';
import { DashboardHelpers } from '@/utils/DashboardHelpers';
import { getWallets, setCurrentWallet } from '@/features/wallets/store/actions/wallet.actions';
import { IUser } from '@/features/user/store/user.model';
import { WALLETS_PER_PAGE } from '@/features/wallets/constants';

export default function useUserWallets(user: IUser) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { userId } = user;

  const wallets = useAppSelector(
    (state) => selectProfileWalletsByUserId(state, userId)?.state?.map((profileWallet) => profileWallet.state?.wallet),
    shallowEqual,
  );

  const [currentPage, setCurrentPage] = useState(1);

  const [filteredWallets, setFilteredWallets] = useState<IWallet[]>([]);

  const [searchValue, setSearchValue] = useState<string>();

  useEffect(() => {
    setFilteredWallets(wallets);
  }, [wallets]);

  const onHandleFilter = useCallback(
    (value: string) => {
      setSearchValue(value);
      const _filteredWallets = wallets.filter(
        (wallet) =>
          wallet.walletId.toString().includes(value) ||
          wallet.eventName.toLocaleLowerCase().includes(value?.toLocaleLowerCase()) ||
          wallet.iban?.toLocaleLowerCase()?.includes(value?.toLocaleLowerCase()),
      );
      setFilteredWallets(_filteredWallets);
    },
    [wallets],
  );

  const error = useAppSelector((state) => selectProfileWalletsByUserId(state, userId)?.error);

  const isLoading = useAppSelector((state) => selectProfileWalletsByUserId(state, userId)?.isLoading);

  const dispatchCurrentWallet = (wallet: IWallet) => {
    dispatch(setCurrentWallet(userId, wallet.walletId));
    navigate(DashboardHelpers.generateProfilePath(userId, { walletId: wallet.walletId }));
  };

  const onPageChange = useCallback(
    (page: number) => {
      setCurrentPage(page);
      dispatch(getWallets(user.userId, true, page, WALLETS_PER_PAGE));
    },
    [dispatch, user.userId],
  );

  return {
    wallets: filteredWallets,
    error,
    isLoading,
    dispatchCurrentWallet,
    onHandleFilter,
    pagination: {
      totalPages: Math.trunc(wallets?.[0]?.totalRows / WALLETS_PER_PAGE || 0) + 1,
      currentPage,
      itemPerPage: WALLETS_PER_PAGE,
    },
    searchValue,
    onPageChange,
  };
}
